import { AbstractFormElement, ICustomFormElement } from "./Abstract";
import { EVENTS } from "../../Modules/Product/Quantity/Constants";

import { InputText } from "./Input";

const KEYCODES = {
    up: "ArrowUp", // win, Mac, Linux
    down: "ArrowDown" // win, Mac, Linux
};

const DEBOUNCE_TIMEOUT = 400;

export class Quantity extends InputText implements ICustomFormElement {
    private plus: HTMLSpanElement;
    private minus: HTMLSpanElement;
    private autoTimeout: NodeJS.Timer;
    private autoInterval: NodeJS.Timer;
    private debounceTimeout: NodeJS.Timer;
    private stopHandler: () => void;

    constructor() {
        super();
        // Override super's click handler
        this.clickHandler = () => {};
        if (!this.hasAttribute("value")) {
            this.setAttribute("value", "1");
        }
        if (!this.hasAttribute("min")) {
            this.setAttribute("min", "1");
        }
        if (!this.hasAttribute("max")) {
            this.setAttribute("max", String(Number.MAX_SAFE_INTEGER));
        }
    }

    connectedCallback() {
        this.initEventListeners();
        super.connectedCallback();
        this.updateButtons();
    }

    /**
     * @inheritDoc
     */
    initElements(): this {
        super.initElements();
        this.minus = document.createElement("span");
        this.minus.innerHTML = "-";
        this.minus.classList.add("minus");
        this.shadow.insertBefore(this.minus, this.input);
        this.plus = document.createElement("span");
        this.plus.innerHTML = "+";
        this.plus.classList.add("plus");
        this.shadow.appendChild(this.plus);
        this.input.setAttribute("type", "number");
        return this;
    }

    /**
     * Store references for event listeners
     * @private
     */
    private initEventListeners() {
        this.stopHandler = this.handlePointerStop.bind(this);
    }

    /**
     * @inheritDoc
     */
    attachEvents(): this {
        super.attachEvents();
        this.input.addEventListener("pointerup", this.input.select);
        this.minus.addEventListener("pointerdown", this.handlePointerMinus.bind(this));
        this.plus.addEventListener("pointerdown", this.handlePointerPlus.bind(this));
        this.minus.addEventListener("pointerup", this.stopHandler);
        this.plus.addEventListener("pointerup", this.stopHandler);
        this.input.addEventListener("beforeinput", Quantity.validateInput);
        this.input.addEventListener("keydown", this.handleKeydown.bind(this));
        this.input.addEventListener("keyup", this.handleKeyup.bind(this));
        this.input.addEventListener("blur", this.handleBlur.bind(this));
        return this;
    }

    updateButtons() {
        if (this.minus && this.plus) {
            this.minus.dataset.disabled = String(this.value === this.min);
            this.plus.dataset.disabled = String(this.value === this.max);
        }
    }

    /**
     * allow only digits and empty string (backspace)
     * @param e
     * @private
     */
    private static validateInput(e: InputEvent) {
        if (e.data && !e.data.match(/\d/)) {
            e.preventDefault();
        }
    }

    /**
     * handle key down events
     * @param e
     */
    private handleKeydown(e: KeyboardEvent) {
        if (e.code === KEYCODES.down) {
            e.preventDefault();
            this.decrement();
        } else if (e.code === KEYCODES.up) {
            e.preventDefault();
            this.increment();
        }
    }

    /**
     * handle key up events
     */
    private handleKeyup() {
        if (this.value != "") {
            if (+this.value < +this.min) {
                this.value = this.min;
            }
            if (+this.value > +this.max) {
                this.value = this.max;
            }
            this.dispatchChange();
        }
    }

    /**
     * set value to min if empty
     * @private
     */
    private handleBlur() {
        if (this.value === "") {
            this.value = this.min;
            this.dispatchChange();
        }
    }

    /**
     * dispatch change event
     * @private
     */
    private dispatchChange() {
        this.updateButtons();
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
        }
        this.debounceTimeout = setTimeout(() => {
            this.dispatchEvent(new CustomEvent(EVENTS.CHANGE, { detail: this.value }));
        }, DEBOUNCE_TIMEOUT);
    }

    /**
     * decrement value if new value is valid
     */
    private decrement() {
        const current = +this.value;
        if (current - 1 >= +this.min) {
            this.value = String(current - 1);
        }
    }

    /**
     * increment value if new value is valid
     */
    private increment() {
        const current = +this.value;
        if (current + 1 <= +this.max) {
            this.value = String(current + 1);
        }
    }

    /**
     * handle pointer down on plus button
     */
    private handlePointerPlus() {
        this.plus.addEventListener("pointermove", this.stopHandler);
        this.plus.addEventListener("pointerleave", this.stopHandler);
        this.input.dispatchEvent(new KeyboardEvent("keydown", { code: KEYCODES.up }));
        this.autoTimeout = setTimeout(() => this.pointerAuto(KEYCODES.up), 500);
    }

    /**
     * handle pointer down on minus button
     */
    private handlePointerMinus() {
        this.minus.addEventListener("pointermove", this.stopHandler);
        this.minus.addEventListener("pointerleave", this.stopHandler);
        this.input.dispatchEvent(new KeyboardEvent("keydown", { code: KEYCODES.down }));
        this.autoTimeout = setTimeout(() => this.pointerAuto(KEYCODES.down), 500);
    }

    /**
     * change value in interval
     * @param code
     */
    private pointerAuto(code: string) {
        this.autoInterval = setInterval(() => {
            this.input.dispatchEvent(new KeyboardEvent("keydown", { code: code }));
        }, 50);
    }

    /**
     * handle pointer up on plus/minus buttons
     */
    private handlePointerStop() {
        clearTimeout(this.autoTimeout);
        clearInterval(this.autoInterval);
        this.minus.removeEventListener("pointermove", this.stopHandler);
        this.plus.removeEventListener("pointermove", this.stopHandler);
        this.plus.removeEventListener("pointerleave", this.stopHandler);
        this.minus.removeEventListener("pointerleave", this.stopHandler);
        this.input.dispatchEvent(new KeyboardEvent("keyup"));
    }

    get value(): string {
        return super.value;
    }

    set value(v: string) {
        super.value = v;
        this.updateButtons();
    }
}

Quantity.prototype.template = document.createElement("template");
Quantity.prototype.shadowStyle = document.createElement("template");
Quantity.prototype.shadowStyle.innerHTML = `
${AbstractFormElement.prototype.shadowStyle.innerHTML}
<style>
span:is(.minus, .plus) {
    background: var(--shadow-input-bg-color, var(--color-white));
    align-self: stretch;
    aspect-ratio: 1;
    display: grid;
    place-content: center;
    user-select: none;
    cursor: pointer;
    font-size: 1.2rem;
}
span[data-disabled="true"] {
    color: hsla(0 0% 0% / .2);
}
input {
    width: var(--input-width);
    background: var(--shadow-input-bg-color, var(--color-white));
    align-self: stretch;
    text-align: center;
    appearance: textfield;
    -moz-appearance: textfield;
}
input::-webkit-inner-spin-button {
    display: none;
}
</style>
`;

Quantity.prototype.template.innerHTML = /*html*/ `
<label class="std-fe">
    <span class="input"></span>
</label>
<span class="error"></span>
`;

customElements.define("x-quantity", Quantity);
